import { useState } from 'react'
import { Button, EditableTable, TableActionButton } from '@tunasong/ui-lib'
import type { EditableColumn } from '@tunasong/ui-lib'
import { Add, Cancel, Delete, Play, Save } from '@tunasong/icons'
import { shortUuid } from '@tunasong/models'
import type { MidiSpec } from '@tunasong/schemas'
import { useFormDialog } from '@tunasong/ui-lib'
import * as z from 'zod'
import { useMidi } from './midi.hook.js'
import styles from './midi-editor.shared.module.css'

interface CCRow {
  id: string
  name: string
  cc: number
  isNew: boolean
}

const toRows = (midi: MidiSpec): CCRow[] =>
  midi.cc.map((cc, index) => ({
    id: index.toString(),
    name: cc.name,
    cc: cc.cc,
    isNew: false,
  }))

const columns: EditableColumn<CCRow>[] = [
  { field: 'name', headerName: 'Name', minWidth: 250, editable: true },
  { field: 'cc', headerName: 'CC', type: 'number', minWidth: 150, editable: true },
]

interface MidiCCGridProps {
  /** use the specified MIDI channel. If not specified, use Omni (all channels) */
  channel?: number
  spec: MidiSpec
  onChange(spec: MidiSpec): void
}

export function MidiCCEditor({ spec, onChange, channel }: MidiCCGridProps) {
  const [rows, setRows] = useState(toRows(spec))
  const [newRowId, setNewRowId] = useState<string | null>(null)
  const { sendCC } = useMidi({ channel })
  const prompt = useFormDialog()

  const saveRows = (updated: CCRow[]) => {
    onChange({
      ...spec,
      cc: updated.map(row => ({
        name: row.name,
        cc: Number(row.cc),
      })),
    })
  }

  const handleAdd = () => {
    const id = shortUuid()
    const newRow: CCRow = { id, name: '', cc: 0, isNew: true }
    setRows(prev => [...prev, newRow])
    setNewRowId(id)
  }

  const handleRowUpdate = (updatedRow: CCRow) => {
    const updatedRows = rows.map(row => (row.id === updatedRow.id ? { ...updatedRow, isNew: false } : row))
    setRows(updatedRows)
    saveRows(updatedRows)
  }

  const handleDelete = (row: CCRow) => {
    const updatedRows = rows.filter(r => r.id !== row.id)
    setRows(updatedRows)
    saveRows(updatedRows)
  }

  const handlePlay = async (row: CCRow) => {
    const formResult = await prompt({
      dialogProps: { title: 'Control Change', maxWidth: 'sm', fullWidth: true },
      schema: z.object({ ccValue: z.number().int().min(0).max(127) }).describe('CC value to send'),
    })
    if (formResult) {
      sendCC({ cc: row.cc, value: formResult.ccValue })
    }
  }

  return (
    <EditableTable
      rows={rows}
      columns={columns}
      autoEditId={newRowId}
      onRowUpdate={handleRowUpdate}
      toolbar={
        <div className={styles.toolbar}>
          <Button color="primary" startIcon={<Add />} onClick={handleAdd}>
            Add CC
          </Button>
        </div>
      }
      renderActions={row => (
        <>
          <TableActionButton icon={<Play />} label="Play" onClick={() => handlePlay(row)} />
          <TableActionButton icon={<Delete />} label="Delete" onClick={() => handleDelete(row)} />
        </>
      )}
      renderEditActions={(_row, onSave, onCancel) => (
        <>
          <TableActionButton icon={<Save />} label="Save" onClick={onSave} />
          <TableActionButton icon={<Cancel />} label="Cancel" onClick={onCancel} />
        </>
      )}
    />
  )
}
